import { ApiError, type Customer, createCustomerSchema, queryKeys } from '@mevaser/shared'
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
  Textarea,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { api } from '../lib/api.js'
import {
  addressFromDraft,
  CustomerAddressFields,
  EMPTY_ADDRESS_DRAFT,
} from './customer-address-fields.js'

export interface ExistingCustomerHit {
  id: string
  name: string
  businessName: string | null
  phone: string | null
  taxExempt: boolean
}

/**
 * Adding someone right from the counter — a customer never has to be found
 * before they can be helped. Nothing is required but a name; a phone or an
 * email can be filled in later from their page, whenever they give one.
 *
 * The host decides what happens with the result: on success it invalidates
 * customer queries, toasts, closes and resets, then hands the created
 * customer to `onCreated` rather than navigating anywhere itself, so it can
 * be opened from inside another in-progress flow (like a sale) without
 * losing that flow's state.
 */
export function AddCustomerDialog({
  open,
  onOpenChange,
  initialName,
  onCreated,
  existingAction,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialName?: string
  onCreated: (customer: Customer) => void
  existingAction: { label: string; onSelect: (customer: ExistingCustomerHit) => void }
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [phone, setPhone] = useState('')
  const [name, setName] = useState('')
  const [businessName, setBusinessName] = useState('')
  const [email, setEmail] = useState('')
  const [address, setAddress] = useState(EMPTY_ADDRESS_DRAFT)
  const [notes, setNotes] = useState('')
  const [source, setSource] = useState('')
  const [externalId, setExternalId] = useState('')
  const [phoneError, setPhoneError] = useState<string | null>(null)
  const [existing, setExisting] = useState<ExistingCustomerHit | null>(null)

  const reset = () => {
    setPhone('')
    setName('')
    setBusinessName('')
    setEmail('')
    setAddress(EMPTY_ADDRESS_DRAFT)
    setNotes('')
    setPhoneError(null)
    setExisting(null)
  }

  // The dialog stays mounted between uses; seed the name each time it opens.
  useEffect(() => {
    if (open) setName(initialName ?? '')
  }, [open, initialName])

  const create = useMutation({
    mutationFn: async () => {
      const parsed = createCustomerSchema.safeParse({
        phone: phone.trim() || null,
        name,
        businessName: businessName.trim() || null,
        email: email.trim() || null,
        address: addressFromDraft(address),
        notes: notes.trim() || null,
        source: source.trim() || null,
        externalId: externalId.trim() || null,
      })
      if (!parsed.success) {
        const issue = parsed.error.issues[0]
        throw new Error(issue?.message ?? 'Check the form and try again')
      }
      return api.post<Customer>('/customers', parsed.data)
    },
    onSuccess: async (customer) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.customers.all() })
      toast.success(`${customer.name} added`)
      onOpenChange(false)
      reset()
      onCreated(customer)
    },
    onError: async (error: Error) => {
      if (error instanceof ApiError && error.status === 409) {
        setPhoneError(error.message)
        const digits = phone.replace(/\D/g, '')
        const q = digits.length >= 3 ? digits : email.trim()
        const found = await api.get<{ items: ExistingCustomerHit[] }>('/customers', {
          query: { q, pageSize: 1 },
        })
        setExisting(found.items[0] ?? null)
        return
      }
      toast.error(error.message)
    },
  })

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next)
        if (!next) reset()
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add customer</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Phone" error={phoneError ?? undefined}>
            {(props) => (
              <Input
                {...props}
                type="tel"
                value={phone}
                onChange={(e) => {
                  setPhone(e.target.value)
                  setPhoneError(null)
                  setExisting(null)
                }}
              />
            )}
          </Field>
          {existing ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                onOpenChange(false)
                reset()
                existingAction.onSelect(existing)
              }}
            >
              {existingAction.label}
            </Button>
          ) : null}
          <Field label="Name" required>
            {(props) => <Input {...props} value={name} onChange={(e) => setName(e.target.value)} />}
          </Field>
          <Field
            label="Business / other identifier"
            hint="Shows in search and next to the name — use it to tell similar accounts apart."
          >
            {(props) => (
              <Input
                {...props}
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
              />
            )}
          </Field>
          <Field label="Email">
            {(props) => (
              <Input
                {...props}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            )}
          </Field>
          <CustomerAddressFields value={address} onChange={setAddress} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Source" hint="Where this record came from, e.g. Square export.">
              {(props) => (
                <Input {...props} value={source} onChange={(e) => setSource(e.target.value)} />
              )}
            </Field>
            <Field label="External ID" hint="Its id in another system, e.g. a Square customer ID.">
              {(props) => (
                <Input
                  {...props}
                  value={externalId}
                  onChange={(e) => setExternalId(e.target.value)}
                />
              )}
            </Field>
          </div>
          <Field
            label="First note"
            hint="Only staff see this. The customer never does. Added to the customer's notes."
          >
            {(props) => (
              <Textarea
                {...props}
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => create.mutate()} disabled={create.isPending}>
            {create.isPending ? 'Adding…' : 'Add customer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
